import type { DateString } from '@/types/date';
import type { ScreeningEntityType } from '@/types/screeningInternational';
import apiClient from '@/api/apiClient';
import type {
  ScreeningAvailabilityResponse,
  ScreeningDecision,
  ScreeningDecisionCreate,
  ScreeningDecisionHistory,
  ScreeningIdentificationReview,
  ScreeningIdentificationOutcome,
  ScreeningDetailResponse,
  ScreeningHistoryResponse,
  ScreeningSearchPayload,
  ScreeningSearchResponse,
  ScreeningPdfResponse,
} from '@/types/screeningInternational';

export interface HistoryParams {
  hasDecision?: boolean;
  hasMatch?: boolean;
  dateFrom?: DateString;
  dateTo?: DateString;
  entityNames?: string[];
  requesterIds?: string[];
  entityType?: ScreeningEntityType;
  isoCountries?: string[];
  limit?: number;
  offset?: number;
  includeFailed?: boolean;
}

const screeningInternationalApi = {
  getStatus(): Promise<ScreeningAvailabilityResponse> {
    return apiClient.get('/screening-international/status');
  },

  getDecision(entityId: string, decisionId: string, signal?: AbortSignal): Promise<ScreeningDecision> {
    return apiClient.get(`/screening-international/entities/${entityId}/decisions/${decisionId}`, { signal });
  },

  async downloadDecisionPdf(entityId: string, decisionId: string, signal?: AbortSignal): Promise<Blob | null> {
    const response = await apiClient.get<Response>(
      `/screening-international/entities/${entityId}/decisions/${decisionId}/pdf`,
      { rawResponse: true, signal },
    );
    if (response.status === 202) return null;
    return response.blob();
  },

  getDecisions(entityId: string, requestId: string, offset = 0): Promise<ScreeningDecisionHistory> {
    return apiClient.get(`/screening-international/entities/${entityId}/decisions`, { params: { requestId, limit: 10, offset } });
  },

  createDecision(entityId: string, payload: ScreeningDecisionCreate, files: File[]): Promise<ScreeningDecision> {
    const body = new FormData();
    body.append('metadata', JSON.stringify(payload));
    files.forEach(file => body.append('files', file));
    return apiClient.post(`/screening-international/entities/${entityId}/decisions`, body);
  },

  async downloadDecisionAttachment(entityId: string, decisionId: string, attachmentId: string): Promise<Blob> {
    const response = await apiClient.get<Response>(
      `/screening-international/entities/${entityId}/decisions/${decisionId}/attachments/${attachmentId}/download`,
      { rawResponse: true },
    );
    return response.blob();
  },

  getIdentification(requestId: string): Promise<ScreeningIdentificationReview> {
    return apiClient.get(`/screening-international/requests/${requestId}/identification`);
  },

  updateIdentification(requestId: string, resultIds: string[], isMatch: boolean | null, expectedUpdatedAt: string): Promise<ScreeningIdentificationReview> {
    return apiClient.patch(`/screening-international/requests/${requestId}/identification/results`, {
      resultIds, isMatch, expectedUpdatedAt,
    });
  },

  confirmIdentification(requestId: string, outcome: ScreeningIdentificationOutcome, expectedUpdatedAt: string, clearMatches = false): Promise<ScreeningIdentificationReview> {
    return apiClient.post(`/screening-international/requests/${requestId}/identification/confirm`, {
      outcome, expectedUpdatedAt, clearMatches,
    });
  },
  getPdfStatus(requestId: string, signal?: AbortSignal): Promise<ScreeningPdfResponse | null> {
    return apiClient.get(`/screening-international/requests/${requestId}/pdf`, { signal });
  },

  async downloadPdf(requestId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await apiClient.get<Response>(
      `/screening-international/requests/${requestId}/pdf/download`,
      { rawResponse: true, signal },
    );
    return response.blob();
  },

  // Runs a new screening (billable).
  search(payload: ScreeningSearchPayload): Promise<ScreeningSearchResponse> {
    return apiClient.post('/screening-international/search', payload);
  },

  // This account's past screening requests.
  getHistory(params: HistoryParams = {}): Promise<ScreeningHistoryResponse> {
    return apiClient.get('/screening-international/history', {
      params: {
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        include_failed: params.includeFailed ?? true,
        has_decision: params.hasDecision,
        has_match: params.hasMatch,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        entity_names: params.entityNames,
        requester_ids: params.requesterIds,
        entity_type: params.entityType,
        iso_countries: params.isoCountries,
      },
    });
  },

  // Re-fetch a stored result by applicant id (NOT billable).
  getResult(applicantId: number | string): Promise<ScreeningDetailResponse> {
    return apiClient.get(`/screening-international/${applicantId}`);
  },

  // Fetch an entity's adverse-media report PDF (proxied through our backend, so
  // no Spearwatch login is needed). Returns a Blob for inline viewing/download.
  async getEntityReportPdf(
    applicantId: number | string,
    entityId: number | string,
  ): Promise<Blob> {
    const response = (await apiClient.get(
      `/screening-international/${applicantId}/entity/${entityId}/report.pdf`,
      { rawResponse: true },
    )) as unknown as Response;
    return response.blob();
  },

  // Fetch the report PDF of an entity RELATED to one of an applicant's matches.
  // `entityId` is the matched entity; `relatedEntityId` is the related party. The
  // backend verifies the relationship belongs to this applicant's paid screening.
  async getRelatedEntityReportPdf(
    applicantId: number | string,
    entityId: number | string,
    relatedEntityId: number | string,
  ): Promise<Blob> {
    const response = (await apiClient.get(
      `/screening-international/${applicantId}/entity/${entityId}/related/${relatedEntityId}/report.pdf`,
      { rawResponse: true },
    )) as unknown as Response;
    return response.blob();
  },
};

export default screeningInternationalApi;
